/**
 * useMobileViewportFix Hook
 *
 * Fixes the issue where mobile browsers dynamically show/hide the address bar,
 * causing the viewport height to change and making fixed-positioned elements
 * (like header and bottom nav) appear to move during scroll.
 *
 * This hook listens for viewport changes and updates CSS custom properties
 * to keep fixed elements properly positioned.
 */

import { useEffect, useRef } from 'react';

interface ViewportFixOptions {
  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Hook to fix mobile viewport issues with fixed positioning
 *
 * @param options - Configuration options
 */
export const useMobileViewportFix = (options: ViewportFixOptions = {}): void => {
  const { debug = false } = options;
  const rafRef = useRef<number | undefined>(undefined);
  const lastHeightRef = useRef<number>(0);
  const isKeyboardVisibleRef = useRef<boolean>(false);

  useEffect(() => {
    // Only apply on mobile devices
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      if (debug) console.log('[useMobileViewportFix] Desktop detected, skipping');
      return;
    }

    if (debug) console.log('[useMobileViewportFix] Initialized on mobile');

    // Detect if virtual keyboard is visible
    const detectKeyboard = (visualHeight: number, windowHeight: number): boolean => {
      // Keyboard is considered visible if visual viewport is significantly smaller
      // than window height (threshold: 150px or more than 20% of window height)
      const heightDiff = windowHeight - visualHeight;
      const threshold = Math.max(150, windowHeight * 0.2);
      return heightDiff > threshold;
    };

    // Update CSS custom properties based on current viewport
    const updateViewportOffset = () => {
      // Get visual viewport (the actual visible viewport)
      const visualViewport = (window as any).visualViewport;
      let currentHeight: number;
      let windowHeight: number;

      if (visualViewport) {
        currentHeight = visualViewport.height;
        windowHeight = window.innerHeight;
      } else {
        currentHeight = window.innerHeight;
        windowHeight = window.innerHeight;
      }

      // Detect keyboard state
      const isKeyboardVisible = detectKeyboard(currentHeight, windowHeight);
      const keyboardStateChanged = isKeyboardVisibleRef.current !== isKeyboardVisible;
      isKeyboardVisibleRef.current = isKeyboardVisible;

      // When keyboard is visible, skip offset updates to prevent jumping
      if (isKeyboardVisible) {
        if (debug && keyboardStateChanged) {
          console.log('[useMobileViewportFix] Keyboard visible, skipping offset updates');
        }
        // Add CSS class for keyboard visible state (used to hide MobileBottomNav)
        if (keyboardStateChanged) {
          document.documentElement.classList.add('mobile-keyboard-visible');
        }
        // Ensure offsets are reset to 0 when keyboard shows
        document.documentElement.style.setProperty('--mobile-header-top', '0px');
        document.documentElement.style.setProperty('--mobile-nav-bottom', '0px');
        return;
      }

      if (debug && keyboardStateChanged) {
        console.log('[useMobileViewportFix] Keyboard hidden, resuming offset updates');
      }

      // Remove CSS class when keyboard is hidden
      if (keyboardStateChanged) {
        document.documentElement.classList.remove('mobile-keyboard-visible');
      }

      // When keyboard just disappeared, reset reference height to current VISUAL height
      // This ensures offset calculation starts from correct baseline (the actual visible area)
      if (keyboardStateChanged && !isKeyboardVisible) {
        lastHeightRef.current = currentHeight;
        // Reset CSS to ensure clean state
        document.documentElement.style.setProperty('--mobile-header-top', '0px');
        document.documentElement.style.setProperty('--mobile-nav-bottom', '0px');
        return; // Skip this update to avoid incorrect offset
      }

      // Store initial height on first run
      if (lastHeightRef.current === 0) {
        lastHeightRef.current = windowHeight;
      }

      // Update CSS custom properties
      // When address bar hides, height increases -> push header up (negative offset)
      // When address bar shows, height decreases -> pull header down (positive offset)
      const headerOffset = Math.max(0, windowHeight - currentHeight);
      const navOffset = Math.max(0, windowHeight - currentHeight);

      document.documentElement.style.setProperty('--mobile-header-top', `${headerOffset}px`);
      document.documentElement.style.setProperty('--mobile-nav-bottom', `${navOffset}px`);

      if (debug) {
        console.log('[useMobileViewportFix] Updated:', {
          windowHeight,
          currentHeight,
          headerOffset,
          navOffset,
        });
      }
    };

    // Initial update
    updateViewportOffset();

    // Listen for visual viewport changes (modern mobile browsers)
    const visualViewport = (window as any).visualViewport;
    if (visualViewport) {
      const handleVisualViewportResize = () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(updateViewportOffset);
      };

      visualViewport.addEventListener('resize', handleVisualViewportResize);
      visualViewport.addEventListener('scroll', handleVisualViewportResize);

      return () => {
        visualViewport.removeEventListener('resize', handleVisualViewportResize);
        visualViewport.removeEventListener('scroll', handleVisualViewportResize);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }

    // Fallback: Listen for window resize and scroll
    const handleResize = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateViewportOffset);
    };

    let scrollTimeout: number;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(updateViewportOffset);
      }, 50); // Debounce scroll events
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase

    if (debug) console.log('[useMobileViewportFix] Event listeners attached');

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      clearTimeout(scrollTimeout);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      // Reset CSS properties
      document.documentElement.style.removeProperty('--mobile-header-top');
      document.documentElement.style.removeProperty('--mobile-nav-bottom');
      document.documentElement.classList.remove('mobile-keyboard-visible');

      if (debug) console.log('[useMobileViewportFix] Cleanup');
    };
  }, [debug]);
};

export default useMobileViewportFix;
